import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Download, Flag, Inbox, Play, Plus } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { BandRing, BandValue } from '@/components/ui/BandScore';
import { Pill, ResultMarker, StatusBadge, Tag } from '@/components/ui/Badge';
import { Button, IconAction, TextLink } from '@/components/ui/Button';
import { Card, CardDivider, Overline } from '@/components/ui/Card';
import { QuestionNavigator, Timer, WordCounter } from '@/components/ui/ExamControls';
import {
  CheckboxRow,
  PasswordField,
  RadioCard,
  TextAreaField,
  TextField,
} from '@/components/ui/Field';
import { ConfirmModal, ModalStat, UpgradeModal } from '@/components/ui/Modal';
import {
  ProgressBar,
  ProgressRow,
  SegmentedControl,
  SegmentedProgress,
  Stepper,
  Tabs,
} from '@/components/ui/Navigation';
import { PageHeader } from '@/components/ui/PageHeader';
import {
  AiProcessing,
  EmptyState,
  IndeterminateBar,
  LoadingLine,
  Skeleton,
  SkeletonCard,
  Spinner,
} from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';

export const Route = createFileRoute('/design-system')({
  // Developer gallery: not served in production builds.
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (!import.meta.env.DEV) throw redirect({ to: '/' });
  },
  component: DesignSystemPage,
});

/**
 * Component gallery for side-by-side comparison with
 * `docs/design/design-system-for-cursor/ds-*.html`. Not linked from navigation.
 */
function DesignSystemPage() {
  const { toast } = useToast();
  const [tab, setTab] = useState('overview');
  const [segment, setSegment] = useState('academic');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [radio, setRadio] = useState('a');

  return (
    <div className="grid gap-10 pb-16">
      <PageHeader
        eyebrow="Reference"
        title="Design system"
        description="Every shared component in its states. Compare with the reference pages in docs/design/design-system-for-cursor."
      />

      <Group title="Buttons">
        <Row>
          <Button>Start test</Button>
          <Button variant="secondary">Save draft</Button>
          <Button variant="ghost">Skip</Button>
          <Button variant="danger">Delete recording</Button>
          <Button disabled>Start test</Button>
          <Button loading>Saving your answers</Button>
        </Row>
        <Row>
          <Button size="sm" icon={Plus}>
            Add note
          </Button>
          <Button size="md" icon={Play}>
            Play audio
          </Button>
          <Button size="lg" icon={Download}>
            Download report
          </Button>
          <IconAction icon={Flag} label="Flag question" />
          <IconAction icon={Flag} label="Flag question" variant="ghost" />
          <TextLink to="/pricing">See plans</TextLink>
        </Row>
      </Group>

      <Group title="Badges and status">
        <Row>
          <Tag tone="academic">Academic</Tag>
          <Tag tone="general">General Training</Tag>
          <Tag tone="primary">New</Tag>
          <Tag tone="locked">Locked</Tag>
          <Pill tone="variant" dot>
            IELTS Academic
          </Pill>
          <Pill tone="ink">Mock test</Pill>
        </Row>
        <Row>
          <StatusBadge kind="correct" />
          <StatusBadge kind="wrong" />
          <StatusBadge kind="weak" />
          <StatusBadge kind="strong" />
          <StatusBadge kind="locked" />
          <StatusBadge kind="flagged" />
          <ResultMarker correct />
          <ResultMarker correct={false} />
        </Row>
      </Group>

      <Group title="Band scores">
        <Row>
          <BandValue value={6.5} size="xs" />
          <BandValue value={null} size="xs" />
          <BandValue value={7} size="sm" />
          <BandValue value={8} size="sm" target />
          <BandValue value={null} size="sm" />
          <BandValue value={6.5} size="md" />
          <BandValue value={6.5} size="lg" />
        </Row>
        <div className="rounded-card bg-ink-panel p-6 text-ink-panel-text">
          <BandRing value={6.5} target={7.5} />
        </div>
      </Group>

      <Group title="Cards">
        <div className="grid gap-4 sm:grid-cols-3">
          <Card padding="lg">
            <Overline>Default</Overline>
            <p className="mt-2 text-body">White surface, 1px border, no shadow at rest.</p>
          </Card>
          <Card padding="lg" interactive>
            <Overline>Interactive</Overline>
            <p className="mt-2 text-body">Lifts on hover. Whole card is one action.</p>
          </Card>
          <Card padding="lg" muted dashed>
            <Overline>Empty slot</Overline>
            <p className="mt-2 text-body">Dashed border for "nothing here yet".</p>
          </Card>
        </div>
      </Group>

      <Group title="Forms">
        <div className="grid max-w-prose gap-4">
          <TextField label="Email" placeholder="you@example.com" />
          <TextField
            label="Email"
            defaultValue="not-an-email"
            error="Enter an email address that includes @, for example you@example.com."
          />
          <TextField label="Candidate number" labelNote="optional" hint="On your booking email." />
          <TextField label="Exam centre" disabled value="Not set" readOnly />
          <PasswordField label="Password" strength={3} hint="At least 8 characters." />
          <TextAreaField
            label="Your answer"
            labelNote="Write at least 250 words."
            rows={5}
            footer={<WordCounter count={180} minimum={250} />}
          />
          <div className="grid gap-2">
            <RadioCard
              title="IELTS Academic"
              description="For university study."
              checked={radio === 'a'}
              onSelect={() => {
                setRadio('a');
              }}
            />
            <RadioCard
              title="IELTS General Training"
              description="For work and migration."
              checked={radio === 'b'}
              onSelect={() => {
                setRadio('b');
              }}
            />
            <RadioCard
              title="PTE Academic"
              description="Not on your plan."
              checked={false}
              disabled
            />
          </div>
          <CheckboxRow
            label="Email me my results"
            checked={checked}
            onToggle={() => {
              setChecked((v) => !v);
            }}
          />
        </div>
      </Group>

      <Group title="Navigation and progress">
        <Tabs
          label="Example"
          value={tab}
          onChange={setTab}
          items={[
            { value: 'overview', label: 'Overview' },
            { value: 'answers', label: 'Answers' },
            { value: 'feedback', label: 'Feedback' },
          ]}
        />
        <SegmentedControl
          label="Variant"
          value={segment}
          onChange={setSegment}
          items={[
            { value: 'academic', label: 'Academic' },
            { value: 'general', label: 'General Training' },
          ]}
        />
        <Stepper
          current={2}
          steps={[{ label: 'Your exam' }, { label: 'Your goal' }, { label: 'Your date' }]}
        />
        <div className="grid max-w-prose gap-4">
          <ProgressRow label="Overall band" hint="target 7.5">
            <ProgressBar value={6.5} max={9} target={7.5 / 9} />
          </ProgressRow>
          <ProgressRow label="Reading" hint="12 of 40 answered">
            <ProgressBar value={12} max={40} tone="reading" />
          </ProgressRow>
          <SegmentedProgress
            segments={[
              { label: 'Part 1', answered: 10, total: 10 },
              { label: 'Part 2', answered: 4, total: 13 },
              { label: 'Part 3', answered: 0, total: 17 },
            ]}
          />
        </div>
      </Group>

      <Group title="Exam controls">
        <Row>
          <Timer secondsRemaining={2400} />
          <Timer secondsRemaining={600} note="10 minutes left" />
          <Timer secondsRemaining={45} note="Finish your answer" />
        </Row>
        <div className="max-w-80">
          <QuestionNavigator
            onSelect={() => undefined}
            onPrevious={() => undefined}
            onNext={() => undefined}
            groups={[
              {
                label: 'Part 1',
                questions: [
                  { number: 1, state: 'answered' },
                  { number: 2, state: 'answered' },
                  { number: 3, state: 'flagged' },
                  { number: 4, state: 'current' },
                  { number: 5, state: 'unanswered' },
                ],
              },
            ]}
          />
        </div>
        <div className="grid max-w-prose gap-2">
          <WordCounter count={180} minimum={250} />
          <WordCounter count={265} minimum={250} />
          <WordCounter count={520} minimum={250} overLimit={400} />
        </div>
      </Group>

      <Group title="Feedback">
        <div className="grid max-w-prose gap-3">
          <Alert tone="info" title="Your answers are saved.">
            You can close this tab and come back later.
          </Alert>
          <Alert tone="success" title="Test submitted.">
            Your feedback will be ready in about a minute.
          </Alert>
          <Alert tone="warning" title="Your microphone is quiet.">
            Move closer to the microphone and record again.
          </Alert>
          <Alert tone="danger" title="We could not save your last answer.">
            Check your connection. We will keep trying.
          </Alert>
        </div>
        <Row>
          <Button
            variant="secondary"
            onClick={() => {
              toast({ message: 'Your answers are saved.' });
            }}
          >
            Show toast
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              toast({ message: 'Your feedback is ready.', tone: 'ai' });
            }}
          >
            Show AI toast
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              toast({ message: 'We could not save that. Check your connection.', tone: 'error' });
            }}
          >
            Show error toast
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setConfirmOpen(true);
            }}
          >
            Show confirm modal
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setUpgradeOpen(true);
            }}
          >
            Show upgrade modal
          </Button>
        </Row>
      </Group>

      <Group title="Loading and empty states">
        <div className="grid gap-4 sm:grid-cols-2">
          <SkeletonCard />
          <Card padding="lg" className="grid gap-4">
            <LoadingLine>Marking your writing. This takes about a minute.</LoadingLine>
            <IndeterminateBar />
            <Row>
              <Spinner />
              <Skeleton className="h-3 w-40" />
            </Row>
          </Card>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <EmptyState
            icon={Inbox}
            title="No tests yet"
            description="Your finished tests will appear here with your band and feedback."
            action={<Button>Start a practice test</Button>}
          />
          <AiProcessing
            title="Marking your essay"
            description="This usually takes under a minute."
            footnote="Uses one AI feedback credit."
            steps={[
              { label: 'Task achievement', state: 'done' },
              { label: 'Coherence and cohesion', state: 'active' },
              { label: 'Lexical resource', state: 'pending' },
              { label: 'Grammatical range and accuracy', state: 'pending' },
            ]}
          />
        </div>
      </Group>

      <ConfirmModal
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          setConfirmOpen(false);
        }}
        title="Submit your reading test?"
        description="You cannot change your answers after you submit."
        stats={
          <div className="grid grid-cols-2 gap-2.5">
            <ModalStat value={3} label="Not answered" />
            <ModalStat value={2} label="Flagged" tone="flag" />
          </div>
        }
      />

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => {
          setUpgradeOpen(false);
        }}
        eyebrow="Plan needed"
        title="Get AI feedback on every test"
        benefits={[
          'Band estimates for writing and speaking',
          'Full mock tests under exam timing',
          'Answer explanations for every question',
        ]}
        primaryLabel="See plans"
        onPrimary={() => {
          setUpgradeOpen(false);
        }}
        secondaryLabel="Not now"
        onSecondary={() => {
          setUpgradeOpen(false);
        }}
        footnote="Prices come from your plan list, never from this screen."
      />
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-4">
      <h2 className="font-display text-h3">{title}</h2>
      <CardDivider />
      {children}
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-3">{children}</div>;
}
